/**
 * useHierarchyTree — lazy, 1C-style parent/is_group tree for hierarchical
 * catalogs in RuntimeListView.
 *
 * Unlike `useGroupTree` (which groups a flat object by arbitrary field
 * values), this hook walks a REAL parent/child hierarchy stored on the
 * object itself (`parent_id` + `is_group`). Root records are fetched with
 * `filters: { parent_id: null }`; expanding a node lazily fetches its
 * children with `filters: { parent_id: <id> }`.
 *
 * Element hierarchy (1C "иерархия групп и элементов"): a plain element
 * (`is_group` falsy) is expandable too when it turns out to have its own
 * children — no different from an ordinary parent/child relationship, just
 * without the group flag. Child-detection is ONE aggregate call per
 * load/reload (`runtimeApi.listGroups` with `group_by: ["parent_id"]`,
 * alongside the roots/reload fetch — never per-row): its group values become
 * `parentsWithChildrenRef`, a `Set<id>` of nodes with at least one child.
 * `is_group` still drives the folder icon + groups-first sort; the summary
 * set only widens who gets a chevron. If the summary call fails, the set
 * stays empty and only declared groups stay expandable (today's behavior) —
 * fail-soft, never blocks the root/child fetch itself.
 *
 * Ownership: a `Map<id, InternalNode>` of every node the caller has seen
 * (root records + fetched children) plus an ordered `childIds` list per
 * expandable node. Collapsing a node drops its cached subtree entirely so
 * re-expanding always re-fetches (mirrors `useGroupTree`'s leaf-cache
 * dispose-on-collapse contract). A generation counter guards against a
 * stale in-flight fetch overwriting state after the query changed.
 *
 * `reload()` soft-refreshes: re-fetches roots + the parent-summary and every
 * currently expanded branch so expansion survives CRUD. `patchRecord`
 * updates one row in place when parent/kind are unchanged; `removeRecord`
 * drops a node without a full tree reset.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'
import type { HierarchyEntry } from '@/shared/listViews/hierarchyTypes'

export type { HierarchyEntry }

export interface HierarchyNodeRow {
  id: string
  parent_id: string | null
  is_group: boolean
  depth: number
  expanded: boolean
  record: Record<string, unknown>
}

export interface UseHierarchyTreeOptions {
  connectionId: string
  objectName: string
  enabled: boolean
  search: string
  searchFields?: string[]
  showDeleted: boolean
  sortField?: string
  sortDir?: 'asc' | 'desc'
  /** Active i18next locale — resolves translatable reference display server-side. */
  locale?: string
}

export interface UseHierarchyTreeResult {
  entries: HierarchyEntry[]
  loading: boolean
  error: string | null
  toggle: (groupId: string) => void
  /** Soft refresh — preserves which groups are expanded. */
  reload: () => void
  /**
   * In-place row update. Returns false when the row is missing or
   * parent_id / is_group changed (caller should `reload()`).
   */
  patchRecord: (id: string, record: Record<string, unknown>) => boolean
  /** Drop a node (and its cached subtree) without resetting expansion elsewhere. */
  removeRecord: (id: string) => void
  /**
   * Walk cached childIds only (null = not loaded → stop).
   * Returns all descendant IDs that have been fetched for the given root.
   */
  loadedDescendantIds: (rootId: string) => string[]
}

interface InternalNode extends HierarchyNodeRow {
  /** Ordered child ids once fetched; `null` while not yet loaded. */
  childIds: string[] | null
}

/** Same normalization as the Predefined boolean/Add-Group spec: true/1/'true'/'1' are truthy. */
function isGroupValue(v: unknown): boolean {
  return v === true || v === 1 || v === 'true' || v === '1'
}

function normalizeParentId(v: unknown): string | null {
  if (v == null || v === '') return null
  return String(v)
}

/**
 * A node is expandable when it's a declared group OR its id shows up in the
 * parent-summary set — a plain element that turns out to have its own
 * children ("element hierarchy", 1C's "иерархия групп и элементов"). Groups
 * stay expandable even when empty (unchanged); a regular element only gains
 * a chevron once the summary confirms it has children.
 */
function isExpandableNode(
  node: Pick<InternalNode, 'id' | 'is_group'>,
  parentsWithChildren: ReadonlySet<string>,
): boolean {
  return node.is_group || parentsWithChildren.has(node.id)
}

/**
 * Groups first within a level, then by name/code when present (server order
 * otherwise). When `sortField` is set, the caller already asked the server
 * to order siblings by that column — trust the API order instead of
 * re-sorting groups-first, or the column sort would appear to do nothing.
 */
function sortRecords(
  rows: Array<Record<string, unknown>>,
  sortField?: string,
): Array<Record<string, unknown>> {
  if (sortField) return [...rows]
  return [...rows].sort((a, b) => {
    const aGroup = isGroupValue(a.is_group)
    const bGroup = isGroupValue(b.is_group)
    if (aGroup !== bGroup) return aGroup ? -1 : 1
    const aKey = String(a.name ?? a.code ?? '')
    const bKey = String(b.name ?? b.code ?? '')
    return aKey.localeCompare(bKey)
  })
}

export function useHierarchyTree(opts: UseHierarchyTreeOptions): UseHierarchyTreeResult {
  const [, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nodesRef = useRef<Map<string, InternalNode>>(new Map())
  const rootIdsRef = useRef<string[] | null>(null)
  const generationRef = useRef(0)
  /** ids that have at least one child, per the parent-summary (`group_by:
   * ["parent_id"]`) fetched once alongside roots/reload. Empty when the
   * summary hasn't loaded yet or failed — degrades to groups-only expansion. */
  const parentsWithChildrenRef = useRef<Set<string>>(new Set())

  // Latest option values for callbacks that run long after render — reading a
  // ref avoids stale closures and dependency churn on every keystroke.
  const stateRef = useRef(opts)
  stateRef.current = opts

  const removeSubtree = useCallback((ids: string[] | null) => {
    if (!ids) return
    for (const id of ids) {
      const node = nodesRef.current.get(id)
      if (node) removeSubtree(node.childIds)
      nodesRef.current.delete(id)
    }
  }, [])

  const collectExpandedIds = useCallback((): string[] => {
    const out: string[] = []
    const walk = (ids: string[] | null) => {
      if (!ids) return
      for (const id of ids) {
        const node = nodesRef.current.get(id)
        if (!node) continue
        if (isExpandableNode(node, parentsWithChildrenRef.current) && node.expanded) {
          out.push(id)
          walk(node.childIds)
        }
      }
    }
    walk(rootIdsRef.current)
    return out
  }, [])

  const applyChildrenRows = useCallback(
    (parentId: string, rows: Array<Record<string, unknown>>, sortField?: string) => {
      const parentNode = nodesRef.current.get(parentId)
      if (!parentNode || !parentNode.expanded) return
      const parentDepth = parentNode.depth
      const sorted = sortRecords(rows, sortField)
      const ids: string[] = []
      for (const row of sorted) {
        const id = String(row.id)
        ids.push(id)
        const prev = nodesRef.current.get(id)
        nodesRef.current.set(id, {
          id,
          parent_id: parentId,
          is_group: isGroupValue(row.is_group),
          depth: parentDepth + 1,
          // Keep expansion if this soft-reload is about to re-open us.
          expanded: prev?.expanded === true,
          record: row,
          childIds: null,
        })
      }
      parentNode.childIds = ids
    },
    [],
  )

  const fetchChildren = useCallback(
    async (parentId: string, generation: number) => {
      const s = stateRef.current
      try {
        const resp = await runtimeApi.list(s.connectionId, s.objectName, {
          filters: { parent_id: parentId },
          search: s.search,
          search_fields: s.searchFields,
          include_deleted: s.showDeleted,
          sort_field: s.sortField,
          sort_dir: s.sortDir,
          locale: s.locale,
        })
        if (generationRef.current !== generation) return
        applyChildrenRows(parentId, resp.data ?? [], s.sortField)
        setError(null)
      } catch (e) {
        if (generationRef.current !== generation) return
        setError(e instanceof Error ? e.message : 'Failed to load children')
      } finally {
        if (generationRef.current === generation) bump()
      }
    },
    [applyChildrenRows, bump],
  )

  const toggle = useCallback(
    (groupId: string) => {
      const node = nodesRef.current.get(groupId)
      if (!node || !isExpandableNode(node, parentsWithChildrenRef.current)) return
      if (node.expanded) {
        node.expanded = false
        removeSubtree(node.childIds)
        node.childIds = null
        bump()
      } else {
        node.expanded = true
        bump()
        if (node.childIds === null) void fetchChildren(groupId, generationRef.current)
      }
    },
    [bump, fetchChildren, removeSubtree],
  )

  const fetchRoots = useCallback(
    async (generation: number) => {
      const s = stateRef.current
      try {
        const resp = await runtimeApi.list(s.connectionId, s.objectName, {
          filters: { parent_id: null },
          search: s.search,
          search_fields: s.searchFields,
          include_deleted: s.showDeleted,
          sort_field: s.sortField,
          sort_dir: s.sortDir,
          locale: s.locale,
        })
        if (generationRef.current !== generation) return
        const rows = sortRecords(resp.data ?? [], s.sortField)
        const nodes = new Map<string, InternalNode>()
        const ids: string[] = []
        for (const row of rows) {
          const id = String(row.id)
          ids.push(id)
          nodes.set(id, {
            id,
            parent_id: null,
            is_group: isGroupValue(row.is_group),
            depth: 0,
            expanded: false,
            record: row,
            childIds: null,
          })
        }
        nodesRef.current = nodes
        rootIdsRef.current = ids
        setError(null)
      } catch (e) {
        if (generationRef.current !== generation) return
        setError(e instanceof Error ? e.message : 'Failed to load records')
      }
    },
    [],
  )

  /**
   * One aggregate call per load/reload: `group_by: ["parent_id"]` returns a
   * summary row per distinct parent value, so its `values.parent_id` set IS
   * "ids that have children" — no per-row requests. Fails soft (empty set)
   * so a summary outage only costs elements their chevron; groups (and the
   * already-successful root/child fetch) are unaffected.
   */
  const fetchParentsWithChildren = useCallback(async (generation: number) => {
    const s = stateRef.current
    try {
      const resp = await runtimeApi.listGroups(s.connectionId, s.objectName, {
        group_by: ['parent_id'],
        search: s.search,
        search_fields: s.searchFields,
        include_deleted: s.showDeleted,
        locale: s.locale,
      })
      if (generationRef.current !== generation) return
      const next = new Set<string>()
      for (const g of resp.groups ?? []) {
        const pid = g.values?.parent_id
        if (pid != null && pid !== '') next.add(String(pid))
      }
      parentsWithChildrenRef.current = next
    } catch {
      if (generationRef.current === generation) parentsWithChildrenRef.current = new Set()
    }
  }, [])

  const softReload = useCallback(async () => {
    // Flat catalogs still mount this hook (enabled=false). reloadActive() used
    // to call us anyway → 422 on parent_id filters after every save.
    if (!stateRef.current.enabled) return
    const expanded = collectExpandedIds()
    const generation = ++generationRef.current
    setLoading(true)
    try {
      await Promise.all([fetchRoots(generation), fetchParentsWithChildren(generation)])
      if (generationRef.current !== generation) return
      for (const id of expanded) {
        if (generationRef.current !== generation) return
        const node = nodesRef.current.get(id)
        if (!node || !isExpandableNode(node, parentsWithChildrenRef.current)) continue
        node.expanded = true
        await fetchChildren(id, generation)
      }
      setError(null)
    } finally {
      if (generationRef.current === generation) setLoading(false)
      bump()
    }
  }, [bump, collectExpandedIds, fetchChildren, fetchParentsWithChildren, fetchRoots])

  const reload = useCallback(() => {
    void softReload()
  }, [softReload])

  const patchRecord = useCallback(
    (id: string, record: Record<string, unknown>): boolean => {
      const node = nodesRef.current.get(id)
      if (!node) return false
      const nextParent = normalizeParentId(record.parent_id)
      const nextGroup = isGroupValue(record.is_group)
      if (nextParent !== normalizeParentId(node.parent_id) || nextGroup !== node.is_group) {
        return false
      }
      node.record = { ...record, id: node.id }
      bump()
      return true
    },
    [bump],
  )

  const removeRecord = useCallback(
    (id: string) => {
      const node = nodesRef.current.get(id)
      if (!node) return
      if (node.parent_id != null) {
        const parent = nodesRef.current.get(node.parent_id)
        if (parent?.childIds) {
          parent.childIds = parent.childIds.filter((cid) => cid !== id)
        }
      } else if (rootIdsRef.current) {
        rootIdsRef.current = rootIdsRef.current.filter((rid) => rid !== id)
      }
      removeSubtree(node.childIds)
      nodesRef.current.delete(id)
      bump()
    },
    [bump, removeSubtree],
  )

  const loadedDescendantIds = useCallback(
    (rootId: string): string[] => {
      const result: string[] = []
      const walk = (nodeId: string) => {
        const node = nodesRef.current.get(nodeId)
        if (!node || !node.childIds) return // null = not loaded, stop
        for (const childId of node.childIds) {
          result.push(childId)
          walk(childId) // Recursively walk descendants
        }
      }
      walk(rootId)
      return result
    },
    [],
  )

  const { connectionId, objectName, enabled, search, showDeleted, sortField, sortDir, locale } = opts
  const searchFieldsKey = (opts.searchFields ?? []).join(',')

  // Hard reset + root load on query change (not on soft reload).
  useEffect(() => {
    generationRef.current += 1
    const generation = generationRef.current
    nodesRef.current = new Map()
    rootIdsRef.current = null
    parentsWithChildrenRef.current = new Set()
    setError(null)

    if (!enabled) {
      setLoading(false)
      bump()
      return
    }

    setLoading(true)
    void (async () => {
      try {
        await Promise.all([fetchRoots(generation), fetchParentsWithChildren(generation)])
      } finally {
        if (generationRef.current === generation) setLoading(false)
        bump()
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    connectionId,
    objectName,
    search,
    searchFieldsKey,
    showDeleted,
    sortField,
    sortDir,
    locale,
  ])

  const flatten = useCallback((): HierarchyEntry[] => {
    const out: HierarchyEntry[] = []
    const parentsWithChildren = parentsWithChildrenRef.current
    const walk = (ids: string[] | null) => {
      if (!ids) return
      for (const id of ids) {
        const node = nodesRef.current.get(id)
        if (!node) continue
        if (node.is_group) {
          out.push({
            kind: 'group',
            id: node.id,
            depth: node.depth,
            expanded: node.expanded,
            record: node.record,
            childCount: node.childIds ? node.childIds.length : undefined,
          })
          if (node.expanded) walk(node.childIds)
        } else {
          // Element hierarchy: a plain element can have its own children too
          // (per the parent-summary) — expandable exactly like a group then.
          const hasChildren = parentsWithChildren.has(node.id)
          out.push({
            kind: 'row',
            id: node.id,
            depth: node.depth,
            record: node.record,
            hasChildren,
            expanded: hasChildren ? node.expanded : undefined,
            childCount: hasChildren && node.childIds ? node.childIds.length : undefined,
          })
          if (hasChildren && node.expanded) walk(node.childIds)
        }
      }
    }
    walk(rootIdsRef.current)
    return out
  }, [])

  return {
    entries: flatten(),
    loading,
    error,
    toggle,
    reload,
    patchRecord,
    removeRecord,
    loadedDescendantIds,
  }
}
